import { $fetch } from 'ofetch';
import type { EventProps } from '~/types/Production';
import { useFavourites } from '~/composables/useFavourites';
import { useAuth } from '~/composables/useAuth';

// Note: /api/cio proxy is configured in server/api/[...].ts (catchAll route)
export type TickslyFavouriteEvent = Pick<EventProps, 'SyncId'>;

// Track ongoing operations to prevent race conditions
const pendingOperations = new Map<string, Promise<any>>();

const addEventToFavourites = async (SyncId: string) => {
  const favouritesState = useFavourites();
  const alreadyPresent = favouritesState.value.favourites.find((f) => f === SyncId);
  if (alreadyPresent) {
    console.warn('Trying to add event to favourites but it already is there');
    return;
  }

  try {
    const tickslyFavouriteEvents = [...favouritesState.value.favourites, SyncId];
    await saveAttributes({ tickslyFavouriteEvents });
    storeFavouritesToState(tickslyFavouriteEvents);
  } catch (error) {
    console.error('Failed to add event to favourites:', error);
    throw error;
  }
};

const removeEventFromFavourites = async (SyncId: string) => {
  const favouritesState = useFavourites();

  try {
    const tickslyFavouriteEvents = favouritesState.value.favourites.filter((f) => f !== SyncId);
    await saveAttributes({ tickslyFavouriteEvents });
    storeFavouritesToState(tickslyFavouriteEvents);
  } catch (error) {
    console.error('Failed to remove event from favourites:', error);
    throw error;
  }
};

const storeFavouritesToState = (favourites: string[]) => {
  const favouritesState = useFavourites();
  favouritesState.value.favourites = favourites;
};

const loadFavourites = async () => {
  try {
    const currentAttributes = await getCustomerAttributes();
    const tickslyFavouriteEvents = JSON.parse(
      currentAttributes.tickslyFavouriteEvents || '[]',
    ) as string[];

    storeFavouritesToState(tickslyFavouriteEvents);
    return tickslyFavouriteEvents;
  } catch (error) {
    console.error('Failed to load favourites:', error);

    // Always ensure we have a valid state, even on error
    const fallbackFavorites: string[] = [];
    storeFavouritesToState(fallbackFavorites);
    return fallbackFavorites;
  }
};

const getUserEmail = (): string => {
  // Try sessionStorage first
  let email = window.sessionStorage.getItem('email');

  // If not in sessionStorage, try to get from auth
  if (!email) {
    const auth = useAuth();
    email = auth.user.value?.email;
  }

  if (!email) {
    throw new Error('User email not found in sessionStorage or auth state');
  }

  return email;
};

// Ensure customer exists in Customer.io before any operations
const ensureCustomerExists = async (email: string): Promise<void> => {
  const operationKey = `ensure-${email}`;

  // Check if there's already an ongoing operation for this email
  if (pendingOperations.has(operationKey)) {
    await pendingOperations.get(operationKey);
    return;
  }

  const operation = (async () => {
    try {
      // First, try to fetch to see if customer exists
      const response = await $fetch(`/api/cio/customers/${email}/attributes`, {
        query: { id_type: 'email' },
      });

      if (response?.customer?.attributes) {
        // Customer exists, we're good
        return;
      }
    } catch (error: any) {
      if (error?.status === 404 || error?.statusCode === 404) {
        // Customer doesn't exist, create them
        console.log('Customer not found, creating new identity for:', email);

        const language = useState('language');
        const initialAttributes = {
          tickslyFavouriteEvents: JSON.stringify([]),
          language_code: String(language.value),
          created_at: new Date().toISOString(),
          source: 'profile_page',
        };

        await createCustomerIdentity(email, initialAttributes);

        // Wait a bit for Customer.io to process the creation
        await new Promise((resolve) => setTimeout(resolve, 2000));
      } else {
        // Some other error - log but don't throw
        console.error('Error checking customer existence:', error);
      }
    }
  })();

  pendingOperations.set(operationKey, operation);

  try {
    await operation;
  } finally {
    pendingOperations.delete(operationKey);
  }
};

// Note: if not found, it will create an identity
const getCustomerAttributes = async (): Promise<Record<string, any>> => {
  const favouritesState = useFavourites();

  try {
    const email = getUserEmail();

    // Ensure customer exists first
    await ensureCustomerExists(email);

    favouritesState.value.isLoading = true;

    // https://customer.io/docs/api/app/?region=eu#operation/getPersonAttributes
    // Now fetch attributes - customer should exist
    const response = await $fetch(`/api/cio/customers/${email}/attributes`, {
      query: { id_type: 'email' },
    });

    favouritesState.value.isLoading = false;
    const attributes = response?.customer?.attributes;

    if (!attributes) {
      console.error('Received an empty attributes response from customerIo', response);
      return getDefaultAttributes();
    }

    return attributes;
  } catch (error: any) {
    favouritesState.value.isLoading = false;

    // After ensuring customer exists, we shouldn't get 404s
    // If we do, just return default attributes
    console.error('Customer.io API error:', error);
    return getDefaultAttributes();
  }
};

const getDefaultAttributes = (): Record<string, any> => {
  const language = useState('language');
  return {
    tickslyFavouriteEvents: JSON.stringify([]),
    language_code: String(language.value),
    created_at: new Date().toISOString(),
    source: 'profile_page',
  };
};

const createCustomerIdentity = async (email: string, attributes: Record<string, any>) => {
  console.log('Creating Customer.io identity for:', email);

  try {
    const response = await $fetch(`/api/cio-track/v2/entity`, {
      method: 'POST',
      body: {
        type: 'person',
        identifiers: {
          email,
        },
        action: 'identify',
        attributes,
      },
    });

    console.log('Customer.io identity created successfully');
    return response;
  } catch (error) {
    console.error('Failed to create Customer.io identity:', error);
    throw error;
  }
};

const saveAttributes = async (attributes: Record<string, any> = {}) => {
  try {
    const email = getUserEmail();
    const favouritesState = useFavourites();

    // Ensure customer exists before saving attributes
    await ensureCustomerExists(email);

    favouritesState.value.isLoading = true;
    await sendIdentifyRequest(email, attributes);
    favouritesState.value.isLoading = false;
  } catch (error) {
    const favouritesState = useFavourites();
    favouritesState.value.isLoading = false;
    console.error('Failed to save attributes:', error);
    throw error;
  }
};

const sendIdentifyRequest = async (email: string, attributes: Record<string, any> = {}) => {
  try {
    // https://customer.io/docs/api/track/#operation/entity
    // note: returns {}
    return await $fetch(`/api/cio-track/v2/entity`, {
      method: 'POST',
      body: {
        type: 'person',
        identifiers: {
          email,
        },
        action: 'identify',
        attributes,
      },
    });
  } catch (error) {
    console.error('Failed to send identify request:', error);
    throw error;
  }
};

const clear = () => {
  const favouritesState = useFavourites();
  favouritesState.value.isLoading = false;
  favouritesState.value.favourites = [];
  // Clear any pending operations
  pendingOperations.clear();
};

export const customerIoAPI = {
  loadFavourites,
  addEventToFavourites,
  removeEventFromFavourites,
  sendIdentifyRequest,
  clear,
};
